import type { Dayjs } from "dayjs";
import type { Currency } from "../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import type { SavedOrder } from "../orders/orderTypes";
import type { Package } from "../sharedComponents/packages/PackageSelect";
import type { CalculationProduct, CalculationProductOption } from "./calculationTypes";

export function getDateRelativeTo(startDate: Dayjs, daysShift: number) {
  return startDate.add(daysShift, "days").format(ISO_DATE_FORMAT);
}

export function getCalculationExchangeRate(
  selectedProducts: { id: string }[],
  calculationCurrency: Currency | undefined,
  products: { id: string; currency: Currency | undefined }[],
) {
  return selectedProducts.every((p) => products.find((pr) => pr.id === p.id)?.currency === calculationCurrency)
    ? 1
    : undefined;
}

export function initializeCalculationFromPackage(
  selectedPackage: Pick<Package, "id" | "days" | "currency" | "margin"> | null | undefined,
  startDate: Dayjs,
) {
  if (!selectedPackage) {
    return {};
  }
  const products: CalculationProduct[] = selectedPackage.days.flatMap((d) =>
    d.products.map((p) => ({
      id: p.id,
      daysShift: d.dayNumber - 1,
      date: getDateRelativeTo(startDate, d.dayNumber - 1),
      options: p.options.map((id) => ({ id, quantity: 0 })),
    })),
  );
  return {
    packageId: selectedPackage.id.id,
    products,
    currency: selectedPackage.currency,
    margin: selectedPackage.margin,
  };
}

export function initializeCalculationFromOrder(order: SavedOrder | null | undefined) {
  if (!order) {
    return {};
  }
  const products: CalculationProduct[] = order.products.map((p) => ({
    id: p.id,
    date: p.date,
    daysShift: dayjs(p.date, ISO_DATE_FORMAT).diff(dayjs(order.start, ISO_DATE_FORMAT), "days"),
    options: p.options.map(({ id, quantity }) => ({ id, quantity })),
  }));
  return {
    name: order.name,
    customerId: order.customerId,
    startDate: order.start,
    participantsNumber: order.participantsNumber,
    products,
    currency: order.currency,
    margin: 0,
  };
}

export function autoFillCalculation<T extends { id: string; options: CalculationProductOption[] }>(
  products: T[],
  totalParticipants: number,
  availableProducts: { id: string; options: { id: string; multiplier: number }[] }[],
): T[] {
  return products.map((product) => {
    const productDetails = availableProducts.find((p) => p.id === product.id);
    return {
      ...product,
      options: product.options.map((option) => {
        if (option.quantity !== 0) {
          return option;
        }
        const multiplier = productDetails?.options.find((o) => o.id === option.id)?.multiplier ?? 1;
        const quantity = Math.ceil(totalParticipants / multiplier);
        return { ...option, quantity };
      }),
    };
  });
}
